"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeSettingsChangeFlags = void 0;
var ai_hub_settings_utils_1 = require("./ai-hub-settings-utils");
var indexBy = function (items) {
    var map = new Map();
    items.forEach(function (item) {
        if (item.__clientId) {
            map.set(item.__clientId, item);
        }
    });
    return map;
};
var recordEntryAt = function (record, index) { return Object.entries(record || {})[index]; };
var isRecordEntryDifferent = function (current, saved, entryIndex, part) {
    var c = recordEntryAt(current, entryIndex);
    var s = recordEntryAt(saved, entryIndex);
    if (part === 'key') {
        return (c && c[0]) !== (s && s[0]);
    }
    return (c && c[1]) !== (s && s[1]);
};
var SYSTEM_VALUE_KEYS = ['id', 'name', 'type', 'baseUrl', 'url', 'sync'];
var WORKFLOW_VALUE_KEYS = ['id', 'name'];
var FIELD_VALUE_KEYS = ['name'];
var MAPPING_VALUE_KEYS = ['system', 'field', 'defaultValue'];
var MAPPING_VALUE_SET_KEYS = ['append', 'format'];
var MAPPING_CONFIG_KEYS = ['userNameSubField', 'emailAddressSubField'];
var computeSystemDiff = function (current, saved) {
    var changedKeys = new Set();
    if (!saved) {
        SYSTEM_VALUE_KEYS.forEach(function (key) {
            if (current[key]) {
                changedKeys.add(key);
            }
        });
        return {
            changedKeys: changedKeys,
            configChanged: Object.keys(current.config || {}).length > 0,
            changed: true,
        };
    }
    SYSTEM_VALUE_KEYS.forEach(function (key) {
        if ((current[key] || '') !== (saved[key] || '')) {
            changedKeys.add(key);
        }
    });
    var configChanged = JSON.stringify(current.config || {}) !== JSON.stringify(saved.config || {});
    return {
        changedKeys: changedKeys,
        configChanged: configChanged,
        changed: changedKeys.size > 0 || configChanged,
    };
};
var computeWorkflowDiff = function (current, saved) {
    var changedKeys = new Set();
    if (!saved) {
        WORKFLOW_VALUE_KEYS.forEach(function (key) {
            if (current[key]) {
                changedKeys.add(key);
            }
        });
        return {
            changedKeys: changedKeys,
            sourcesChanged: (current.sources || []).length > 0,
            targetsChanged: (current.targets || []).length > 0,
            changed: true,
        };
    }
    WORKFLOW_VALUE_KEYS.forEach(function (key) {
        if ((current[key] || '') !== (saved[key] || '')) {
            changedKeys.add(key);
        }
    });
    var sourcesChanged = JSON.stringify(current.sources || []) !== JSON.stringify(saved.sources || []);
    var targetsChanged = JSON.stringify(current.targets || []) !== JSON.stringify(saved.targets || []);
    return {
        changedKeys: changedKeys,
        sourcesChanged: sourcesChanged,
        targetsChanged: targetsChanged,
        changed: changedKeys.size > 0 || sourcesChanged || targetsChanged,
    };
};
var computeMappingDiff = function (current, saved) {
    var result = {
        changedValueKeys: new Set(),
        changedValueSetKeys: new Set(),
        changedConfigKeys: new Set(),
        changedMapKeys: new Set(),
        changedMapValues: new Set(),
    };
    MAPPING_VALUE_KEYS.forEach(function (key) {
        if ((current[key] || '') !== ((saved && saved[key]) || '')) {
            result.changedValueKeys.add(key);
        }
    });
    MAPPING_VALUE_SET_KEYS.forEach(function (key) {
        if (key === 'append') {
            var c = !!(current.valueSet && current.valueSet.append);
            var s = !!(saved && saved.valueSet && saved.valueSet.append);
            if (c !== s) {
                result.changedValueSetKeys.add(key);
            }
        }
        else {
            var c = (current.valueSet && current.valueSet.format) || '';
            var s = (saved && saved.valueSet && saved.valueSet.format) || '';
            if (c !== s) {
                result.changedValueSetKeys.add(key);
            }
        }
    });
    MAPPING_CONFIG_KEYS.forEach(function (key) {
        var c = (current.config && current.config[key]) || '';
        var s = (saved && saved.config && saved.config[key]) || '';
        if (c !== s) {
            result.changedConfigKeys.add(key);
        }
    });
    var entryCount = Math.max(Object.keys(current.map || {}).length, Object.keys((saved && saved.map) || {}).length);
    for (var entryIndex = 0; entryIndex < entryCount; entryIndex += 1) {
        if (isRecordEntryDifferent(current.map, saved && saved.map, entryIndex, 'key')) {
            result.changedMapKeys.add(entryIndex);
        }
        if (isRecordEntryDifferent(current.map, saved && saved.map, entryIndex, 'value')) {
            result.changedMapValues.add(entryIndex);
        }
    }
    return result;
};
var computeFieldDiff = function (current, saved) {
    var changedFieldKeys = new Set();
    FIELD_VALUE_KEYS.forEach(function (key) {
        if ((current[key] || '') !== ((saved && saved[key]) || '')) {
            changedFieldKeys.add(key);
        }
    });
    var ignoreChanged = !!current.ignore !== !!(saved && saved.ignore);
    var currentSystems = current.mapping.map(function (mapping) { return mapping.system; }).filter(Boolean);
    var savedSystems = ((saved && saved.mapping) || [])
        .map(function (mapping) { return mapping.system; })
        .filter(Boolean);
    var mappedSystemsChanged = JSON.stringify(currentSystems) !== JSON.stringify(savedSystems);
    var savedMappingsById = saved
        ? indexBy(saved.mapping)
        : new Map();
    var mappings = new Map();
    var hasMappingChanges = false;
    current.mapping.forEach(function (mapping, mappingIndex) {
        var id = mapping.__clientId || "__index_" + mappingIndex;
        var savedMapping = (mapping.__clientId && savedMappingsById.get(mapping.__clientId)) ||
            (saved && saved.mapping[mappingIndex]);
        var diff = computeMappingDiff(mapping, savedMapping);
        mappings.set(id, diff);
        if (diff.changedValueKeys.size +
            diff.changedValueSetKeys.size +
            diff.changedConfigKeys.size +
            diff.changedMapKeys.size +
            diff.changedMapValues.size >
            0) {
            hasMappingChanges = true;
        }
    });
    if (saved && saved.mapping.length !== current.mapping.length) {
        hasMappingChanges = true;
    }
    return {
        changedFieldKeys: changedFieldKeys,
        ignoreChanged: ignoreChanged,
        mappedSystemsChanged: mappedSystemsChanged,
        changed: changedFieldKeys.size > 0 ||
            ignoreChanged ||
            mappedSystemsChanged ||
            hasMappingChanges ||
            !saved,
        mappings: mappings,
    };
};
var buildSystemConfigEntryDiffs = function (current, saved) {
    var keys = new Set();
    var values = new Set();
    var entryCount = Math.max(Object.keys(current.config || {}).length, Object.keys((saved && saved.config) || {}).length);
    for (var i = 0; i < entryCount; i += 1) {
        if (isRecordEntryDifferent(current.config, saved && saved.config, i, 'key')) {
            keys.add(i);
        }
        if (isRecordEntryDifferent(current.config, saved && saved.config, i, 'value')) {
            values.add(i);
        }
    }
    return { keys: keys, values: values };
};
var EMPTY_SYSTEM_DIFF = {
    changedKeys: new Set(),
    configChanged: false,
    changed: false,
};
var EMPTY_WORKFLOW_DIFF = {
    changedKeys: new Set(),
    sourcesChanged: false,
    targetsChanged: false,
    changed: false,
};
var EMPTY_FIELD_DIFF = {
    changedFieldKeys: new Set(),
    ignoreChanged: false,
    mappedSystemsChanged: false,
    changed: false,
    mappings: new Map(),
};
var EMPTY_MAPPING_DIFF = {
    changedValueKeys: new Set(),
    changedValueSetKeys: new Set(),
    changedConfigKeys: new Set(),
    changedMapKeys: new Set(),
    changedMapValues: new Set(),
};
/**
 * Compute every "is changed" boolean the editor needs in a single pass over
 * draft + saved. Returns id-keyed lookup functions so individual rows do not
 * re-walk the trees on every render.
 */
exports.computeSettingsChangeFlags = function (draft, saved) {
    var savedSystemsById = indexBy(saved.systems);
    var savedFieldsById = indexBy(saved.fields);
    var systemDiffs = new Map();
    var systemConfigEntryDiffs = new Map();
    var hasAnyChanges = false;
    draft.systems.forEach(function (system, index) {
        var id = system.__clientId || "__sys_" + index;
        var savedSystem = (system.__clientId && savedSystemsById.get(system.__clientId)) || saved.systems[index];
        var diff = computeSystemDiff(system, savedSystem);
        systemDiffs.set(id, diff);
        systemConfigEntryDiffs.set(id, buildSystemConfigEntryDiffs(system, savedSystem));
        if (diff.changed) {
            hasAnyChanges = true;
        }
    });
    if (draft.systems.length !== saved.systems.length) {
        hasAnyChanges = true;
    }
    var savedWorkflowsById = indexBy(saved.workflows || []);
    var workflowDiffs = new Map();
    (draft.workflows || []).forEach(function (workflow, index) {
        var id = workflow.__clientId || "__wfl_" + index;
        var savedWorkflow = (workflow.__clientId && savedWorkflowsById.get(workflow.__clientId)) ||
            (saved.workflows || [])[index];
        var diff = computeWorkflowDiff(workflow, savedWorkflow);
        workflowDiffs.set(id, diff);
        if (diff.changed) {
            hasAnyChanges = true;
        }
    });
    if ((draft.workflows || []).length !== (saved.workflows || []).length) {
        hasAnyChanges = true;
    }
    var fieldDiffs = new Map();
    draft.fields.forEach(function (field, index) {
        var id = field.__clientId || "__fld_" + index;
        var savedField = (field.__clientId && savedFieldsById.get(field.__clientId)) || saved.fields[index];
        var diff = computeFieldDiff(field, savedField);
        fieldDiffs.set(id, diff);
        if (diff.changed) {
            hasAnyChanges = true;
        }
    });
    if (draft.fields.length !== saved.fields.length) {
        hasAnyChanges = true;
    }
    // Final, normalized whole-schema check protects against false negatives
    // (e.g. reorderings) without affecting the per-row flags above.
    if (!hasAnyChanges) {
        hasAnyChanges =
            JSON.stringify(ai_hub_settings_utils_1.sanitizeAiHubSettingsSchema(draft)) !==
                JSON.stringify(ai_hub_settings_utils_1.sanitizeAiHubSettingsSchema(saved));
    }
    var getSystemDiff = function (id) { return systemDiffs.get(id) || EMPTY_SYSTEM_DIFF; };
    var getFieldDiff = function (id) { return fieldDiffs.get(id) || EMPTY_FIELD_DIFF; };
    var getMappingDiff = function (fieldId, mappingId) {
        return getFieldDiff(fieldId).mappings.get(mappingId) || EMPTY_MAPPING_DIFF;
    };
    var getWorkflowDiff = function (id) {
        return workflowDiffs.get(id) || EMPTY_WORKFLOW_DIFF;
    };
    return {
        hasAnyChanges: hasAnyChanges,
        isSystemChanged: function (id) { return getSystemDiff(id).changed; },
        isSystemValueChanged: function (id, key) { return getSystemDiff(id).changedKeys.has(key); },
        isSystemConfigChanged: function (id) { return getSystemDiff(id).configChanged; },
        isSystemConfigEntryChanged: function (id, entryIndex, part) {
            var entry = systemConfigEntryDiffs.get(id);
            if (!entry) {
                return false;
            }
            return part === 'key' ? entry.keys.has(entryIndex) : entry.values.has(entryIndex);
        },
        isWorkflowChanged: function (id) { return getWorkflowDiff(id).changed; },
        isWorkflowValueChanged: function (id, key) { return getWorkflowDiff(id).changedKeys.has(key); },
        isWorkflowSystemsChanged: function (id, role) {
            return role === 'sources' ? getWorkflowDiff(id).sourcesChanged : getWorkflowDiff(id).targetsChanged;
        },
        isFieldChanged: function (id) { return getFieldDiff(id).changed; },
        isFieldValueChanged: function (id, key) { return getFieldDiff(id).changedFieldKeys.has(key); },
        isFieldIgnoreChanged: function (id) { return getFieldDiff(id).ignoreChanged; },
        isFieldMappedSystemsChanged: function (id) { return getFieldDiff(id).mappedSystemsChanged; },
        isMappingValueChanged: function (fieldId, mappingId, key) {
            return getMappingDiff(fieldId, mappingId).changedValueKeys.has(key);
        },
        isMappingValueSetChanged: function (fieldId, mappingId, key) {
            return getMappingDiff(fieldId, mappingId).changedValueSetKeys.has(key);
        },
        isMappingConfigChanged: function (fieldId, mappingId, key) {
            return getMappingDiff(fieldId, mappingId).changedConfigKeys.has(key);
        },
        isMappingMapEntryChanged: function (fieldId, mappingId, entryIndex, part) {
            var diff = getMappingDiff(fieldId, mappingId);
            return part === 'key'
                ? diff.changedMapKeys.has(entryIndex)
                : diff.changedMapValues.has(entryIndex);
        },
    };
};
